import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabase';

// ── Launch state ───────────────────────────────────────────────────────────
// false = pre-launch waitlist page (MysteryLanding). true = full launched site.
// WORKFLOW: keep `true` while building/previewing the launched page, but set it
// back to `false` before deploying — the public site should stay on the waitlist
// until launch day, when this flips to `true` for real (the App Store link must be
// live by then; see APP_STORE_URL below).
const LAUNCHED = false;

// Real App Store URL is injected via env once the app is approved
// (REACT_APP_APP_STORE_URL = https://apps.apple.com/app/id<APPLE_ID>). Until it's
// set, DownloadButton shows a "Coming soon to iOS" state instead of a dead link.
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || '';
// Single "is the app actually downloadable" signal — drives BOTH the hero badge and
// the download button so they never contradict each other.
const APP_LIVE = Boolean(APP_STORE_URL);

// Single source of truth for the App Store CTA — link when live, "coming soon"
// (non-clickable) when the URL isn't set yet. Used in the nav, hero, and final CTA.
function DownloadButton({ style, label = 'Download on the App Store →' }) {
  const base = { textDecoration: 'none', display: 'inline-block', textAlign: 'center', ...style };
  if (APP_STORE_URL) {
    return (
      <a className="btn-primary" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" style={base}>{label}</a>
    );
  }
  return (
    <span className="btn-primary" style={{ ...base, opacity: 0.6, cursor: 'default' }}>Coming soon to iOS</span>
  );
}

// Hide the live waitlist count until it's big enough to be social proof.
const MIN_PUBLIC_WAITLIST_COUNT = 50;

// ── Aurora tokens ─────────────────────────────────────────────────────────────
const C = {
  bg: '#030010',
  surface: 'rgba(255,255,255,0.04)',
  card: 'rgba(8,4,28,0.7)',
  border: 'rgba(255,255,255,0.08)',
  borderGlow: 'rgba(167,139,250,0.3)',
  text: '#f0ecff',
  muted: 'rgba(255,255,255,0.5)',
  dim: 'rgba(255,255,255,0.25)',
  grad: {
    primary: 'linear-gradient(135deg,#a78bfa,#f472b6)',
    text: 'linear-gradient(90deg,#a78bfa,#f472b6,#fb923c)',
    gold: 'linear-gradient(135deg,#fbbf24,#f59e0b)',
    green: 'linear-gradient(135deg,#34d399,#06b6d4)',
  },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,500;0,700;0,900;1,300;1,500;1,700&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: #030010; color: #f0ecff; font-family: 'DM Sans', sans-serif; overflow-x: hidden; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #030010; }
  ::-webkit-scrollbar-thumb { background: rgba(167,139,250,0.3); border-radius: 2px; }
  @keyframes aurora1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(4%,-3%) scale(1.04)} 66%{transform:translate(-3%,4%) scale(.97)} }
  @keyframes aurora2 { 0%,100%{transform:translate(0,0)} 33%{transform:translate(-4%,5%)} 66%{transform:translate(5%,-3%)} }
  @keyframes aurora3 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(3%,-4%)} }
  @keyframes shimmer { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }

  /* Mobile responsive */
  @media (max-width: 640px) {
    .mystery-hero { font-size: 52px !important; letter-spacing: -2px !important; line-height: 1.05 !important; }
    .mystery-logo { font-size: 24px !important; letter-spacing: -0.5px !important; }
    .footer-row { flex-direction: column !important; gap: 24px !important; text-align: center !important; align-items: center !important; }
    .footer-links { gap: 24px !important; }
    .footer-copyright { font-size: 11px !important; }
    .waitlist-form-row { flex-direction: column !important; gap: 12px !important; }
    .waitlist-btn { width: 100% !important; padding: 14px !important; }
  }

  /* Launched-page responsive */
  @media (max-width: 960px) {
    .nav-center { display: none !important; }
    .hero-grid { grid-template-columns: 1fr !important; gap: 56px !important; }
    .hiw-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
  }
  @media (max-width: 640px) {
    .section-pad { padding-left: 20px !important; padding-right: 20px !important; padding-top: 72px !important; padding-bottom: 72px !important; }
    .hero-h1 { font-size: 46px !important; letter-spacing: -2px !important; line-height: 1.02 !important; }
    .section-h2 { font-size: 36px !important; letter-spacing: -1.5px !important; }
    .hero-cta-row { flex-direction: column !important; align-items: stretch !important; }
    .cta-card { padding: 48px 24px !important; }
    .float-badge { display: none !important; }
  }
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes pulse { 0%,100%{opacity:.6} 50%{opacity:1} }
  @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  @keyframes ping { 0%{transform:scale(1);opacity:1} 100%{transform:scale(2.2);opacity:0} }
  .fade-up { opacity: 0; transform: translateY(28px); transition: opacity .7s ease, transform .7s ease; }
  .fade-up.visible { opacity: 1; transform: translateY(0); }
  .grad-text { background: linear-gradient(90deg,#a78bfa,#f472b6,#fb923c); background-size: 200%; -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: shimmer 5s linear infinite; }
  .glass { background: rgba(8,4,28,0.7); backdrop-filter: blur(40px); -webkit-backdrop-filter: blur(40px); border: 1px solid rgba(255,255,255,0.08); }
  .glow-border { border: 1px solid rgba(167,139,250,0.3); box-shadow: 0 0 32px rgba(167,139,250,0.1), inset 0 1px 0 rgba(255,255,255,0.08); }
  .btn-primary { background: linear-gradient(135deg,#a78bfa,#f472b6); border: none; border-radius: 14px; padding: 15px 32px; font-family: 'DM Sans',sans-serif; font-size: 15px; font-weight: 700; color: #fff; cursor: pointer; box-shadow: 0 8px 32px rgba(167,139,250,0.4); transition: all .3s; }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 16px 48px rgba(167,139,250,0.5); }
  .btn-ghost { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); border-radius: 14px; padding: 15px 32px; font-family: 'DM Sans',sans-serif; font-size: 15px; font-weight: 600; color: rgba(255,255,255,0.8); cursor: pointer; transition: all .3s; }
  .btn-ghost:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); }
  .nav-link { color: rgba(255,255,255,0.6); font-size: 14px; font-weight: 500; text-decoration: none; cursor: pointer; transition: color .2s; }
  .nav-link:hover { color: #f0ecff; }
  .feature-card { background: rgba(8,4,28,0.6); border: 1px solid rgba(255,255,255,0.07); border-radius: 24px; padding: 28px; backdrop-filter: blur(30px); transition: all .4s; cursor: default; }
  .feature-card:hover { border-color: rgba(167,139,250,0.3); box-shadow: 0 24px 64px rgba(167,139,250,0.1), inset 0 1px 0 rgba(255,255,255,0.08); transform: translateY(-4px); }
  .step-line { position: absolute; left: 23px; top: 52px; bottom: -28px; width: 1px; background: linear-gradient(to bottom, rgba(167,139,250,0.4), transparent); }
  .testimonial-card { background: rgba(8,4,28,0.6); border: 1px solid rgba(255,255,255,0.07); border-radius: 20px; padding: 24px; backdrop-filter: blur(20px); flex-shrink: 0; width: 320px; transition: all .3s; }
  .testimonial-card:hover { border-color: rgba(167,139,250,0.25); transform: translateY(-3px); }
  .phone-mockup { width: 280px; height: 560px; border-radius: 38px; border: 6px solid rgba(255,255,255,0.1); background: #030010; overflow: hidden; box-shadow: 0 40px 100px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.05), inset 0 0 0 1px rgba(255,255,255,0.03); position: relative; animation: float 6s ease-in-out infinite; }
  .phone-notch { position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 90px; height: 24px; background: rgba(255,255,255,0.07); border-radius: 0 0 16px 16px; z-index: 10; }
  .waitlist-input { transition: border-color .25s, box-shadow .25s, background .25s; }
  .waitlist-input::placeholder { color: rgba(255,255,255,0.35); }
  .waitlist-input:focus { border-color: rgba(167,139,250,0.55) !important; box-shadow: 0 0 0 4px rgba(167,139,250,0.12); background: rgba(255,255,255,0.06) !important; }
`;

// ── Live waitlist count ───────────────────────────────────────────────────────
// Reads the aggregate via the waitlist_count() RPC (security definer — the
// table itself is admin-select-only). Returns null until loaded, on error,
// or while the count is below MIN_PUBLIC_WAITLIST_COUNT.
function useWaitlistCount() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let active = true;
    supabase.rpc('waitlist_count').then(({ data, error }) => {
      if (active && !error && typeof data === 'number' && data >= MIN_PUBLIC_WAITLIST_COUNT) {
        setCount(data);
      }
    });
    return () => { active = false; };
  }, []);
  return count;
}

// ── Intersection observer hook ────────────────────────────────────────────────
function useVisible(threshold = 0.15) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeUp({ children, delay = 0, style = {} }) {
  const [ref, visible] = useVisible();
  return (
    <div ref={ref} className={`fade-up${visible ? ' visible' : ''}`} style={{ transitionDelay: `${delay}ms`, ...style }}>
      {children}
    </div>
  );
}

// ── Aurora Background ─────────────────────────────────────────────────────────
function AuroraBg({ fixed = false }) {
  const pos = fixed ? 'fixed' : 'absolute';
  return (
    <>
      <div style={{ position: pos, top: '-20%', left: '-10%', width: '60%', height: '60%', borderRadius: '50%', background: 'radial-gradient(circle,rgba(99,102,241,0.55) 0%,rgba(168,85,247,0.25) 40%,transparent 70%)', animation: 'aurora1 18s ease-in-out infinite', filter: 'blur(70px)', mixBlendMode: 'screen', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: pos, top: '20%', right: '-15%', width: '55%', height: '55%', borderRadius: '50%', background: 'radial-gradient(circle,rgba(236,72,153,0.45) 0%,rgba(251,146,60,0.15) 40%,transparent 70%)', animation: 'aurora2 22s ease-in-out infinite', filter: 'blur(65px)', mixBlendMode: 'screen', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: pos, bottom: '-10%', left: '30%', width: '50%', height: '50%', borderRadius: '50%', background: 'radial-gradient(circle,rgba(52,211,153,0.35) 0%,rgba(59,130,246,0.15) 40%,transparent 70%)', animation: 'aurora3 15s ease-in-out infinite', filter: 'blur(60px)', mixBlendMode: 'screen', pointerEvents: 'none', zIndex: 0 }} />
    </>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────────────
function Nav({ onSignIn }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '0 40px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: scrolled ? 'rgba(3,0,16,0.85)' : 'transparent', backdropFilter: scrolled ? 'blur(20px)' : 'none', borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none', transition: 'all .4s' }}>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: 24, fontWeight: 900, letterSpacing: -1 }}>
        <span className="grad-text">candid.</span>
      </div>
      <div className="nav-center" style={{ display: 'flex', gap: 36, alignItems: 'center' }}>
        {['Features', 'How it works'].map(label => (
          <a key={label} href={`#${label.toLowerCase().replace(/ /g, '-')}`} className="nav-link">{label}</a>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {onSignIn && <button className="btn-ghost" onClick={onSignIn} style={{ padding: '10px 22px', fontSize: 13 }}>Sign in</button>}
        <DownloadButton style={{ padding: '10px 22px', fontSize: 13 }} label="Download for iOS →" />
      </div>
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero({ onSignIn }) {
  const waitlistCount = useWaitlistCount();
  return (
    <section className="section-pad" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '120px 40px 80px', position: 'relative', overflow: 'hidden' }}>
      <AuroraBg />
      {/* Noise */}
      <div style={{ position: 'absolute', inset: 0, opacity: .025, backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")", backgroundSize: '150px', pointerEvents: 'none', zIndex: 0 }} />

      <div className="hero-grid" style={{ position: 'relative', zIndex: 1, maxWidth: 1200, width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
        {/* Left */}
        <div>
          {/* Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 50, padding: '6px 16px', marginBottom: 32, animation: 'fadeUp .6s ease both' }}>
            <span style={{ position: 'relative', display: 'inline-block', width: 8, height: 8 }}>
              <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#a78bfa', animation: 'ping 1.5s infinite' }} />
              <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#a78bfa' }} />
            </span>
            <span style={{ fontSize: 12, color: '#c4b5fd', fontWeight: 600, letterSpacing: .5 }}>{APP_LIVE ? 'Now live on iOS' : 'Coming soon · iOS first'}</span>
          </div>

          <h1 className="hero-h1" style={{ fontFamily: 'Fraunces, serif', fontSize: 76, fontWeight: 900, letterSpacing: -4, lineHeight: .92, marginBottom: 28, animation: 'fadeUp .6s ease .1s both' }}>
            Say what you<br />
            <span className="grad-text">actually want.</span>
          </h1>

          <p style={{ fontSize: 18, color: C.muted, lineHeight: 1.8, marginBottom: 40, maxWidth: 460, animation: 'fadeUp .6s ease .2s both', fontWeight: 300 }}>
            The only dating app where your <strong style={{ color: C.text, fontWeight: 600 }}>reputation for honesty</strong> follows you. No mixed signals. No wasted time. Just real people being real.
          </p>

          <div className="hero-cta-row" style={{ display: 'flex', gap: 14, marginBottom: 48, animation: 'fadeUp .6s ease .3s both' }}>
            <DownloadButton style={{ fontSize: 16, padding: '16px 36px' }} />
            <a className="btn-ghost" href="#how-it-works" style={{ fontSize: 16, padding: '16px 28px', textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}>See how it works</a>
          </div>

          {/* Intentions — the real ones from the app */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, animation: 'fadeUp .6s ease .4s both', maxWidth: 460 }}>
            {['☕ Regular hangout buddy', '🌊 Casual, no strings', '🌙 Something in between', '🌱 Real relationship', '🤷 Honestly not sure yet'].map(label => (
              <span key={label} style={{ fontSize: 12, color: C.muted, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '6px 14px', fontWeight: 500 }}>{label}</span>
            ))}
          </div>

          {/* Real social proof — live waitlist count */}
          {waitlistCount !== null && (
            <p style={{ fontSize: 13, color: C.muted, marginTop: 20, animation: 'fadeIn .6s ease both' }}>
              <strong style={{ color: C.text }}>{waitlistCount.toLocaleString()}</strong> people signed up before we launched.
            </p>
          )}
        </div>

        {/* Right — Phone mockup */}
        <div style={{ display: 'flex', justifyContent: 'center', position: 'relative', animation: 'fadeIn .8s ease .3s both' }}>
          {/* Glow behind phone */}
          <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(167,139,250,0.3) 0%,transparent 70%)', filter: 'blur(40px)', animation: 'pulse 3s infinite' }} />

          <div className="phone-mockup">
            <div className="phone-notch" />
            {/* Phone UI preview */}
            <div style={{ width: '100%', height: '100%', background: 'linear-gradient(180deg,#0d0520 0%,#030010 100%)', padding: '32px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontFamily: 'Fraunces,serif', fontSize: 18, fontWeight: 900, background: C.grad.text, backgroundSize: '200%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmer 5s linear infinite' }}>candid.</span>
                <span style={{ fontSize: 10, color: 'rgba(251,191,36,0.8)', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 20, padding: '2px 8px' }}>☕ hangout</span>
              </div>
              {/* Profile card */}
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, overflow: 'hidden', flex: 1 }}>
                <div style={{ height: 160, background: 'linear-gradient(135deg,rgba(99,102,241,0.3),rgba(236,72,153,0.2))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 64, filter: 'blur(0px)', position: 'relative' }}>
                  🧑‍🎤
                  <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 20, padding: '2px 8px', fontSize: 9, color: '#34d399', fontWeight: 700 }}>✓ verified</div>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 40, background: 'linear-gradient(transparent,rgba(3,0,16,0.9))' }} />
                </div>
                <div style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Jordan, 28</div>
                      <div style={{ fontSize: 10, color: C.dim }}>0.4 mi away</div>
                    </div>
                    <div style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 20, padding: '3px 10px', fontSize: 10, color: '#34d399', fontWeight: 700, alignSelf: 'flex-start' }}>Trusted</div>
                  </div>
                  <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 8, padding: '5px 10px', display: 'inline-flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
                    <span style={{ fontSize: 12 }}>☕</span>
                    <span style={{ fontSize: 10, color: '#fbbf24', fontWeight: 700 }}>Regular hangout buddy</span>
                  </div>
                  <p style={{ fontSize: 10, color: C.muted, lineHeight: 1.5 }}>Guitarist, long walks, longer conversations. I mean what I say here.</p>
                </div>
              </div>
              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✕</div>
                <div style={{ flex: 1, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#a78bfa,#f472b6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff' }}>✦ Connect</div>
              </div>
            </div>
          </div>

          {/* Floating badges */}
          <div className="float-badge" style={{ position: 'absolute', top: '15%', right: '-5%', background: 'rgba(8,4,28,0.9)', backdropFilter: 'blur(20px)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 14, padding: '10px 14px', animation: 'float 5s ease-in-out infinite' }}>
            <div style={{ fontSize: 11, color: '#34d399', fontWeight: 700, marginBottom: 2 }}>✓ Selfie verified</div>
            <div style={{ fontSize: 10, color: C.muted }}>reviewed by a human</div>
          </div>
          <div className="float-badge" style={{ position: 'absolute', bottom: '20%', left: '-8%', background: 'rgba(8,4,28,0.9)', backdropFilter: 'blur(20px)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 14, padding: '10px 14px', animation: 'float 4s ease-in-out 1s infinite' }}>
            <div style={{ fontSize: 11, color: '#fbbf24', fontWeight: 700, marginBottom: 2 }}>⭐ Reputation</div>
            <div style={{ fontSize: 10, color: C.muted }}>Trusted · earned, not claimed</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Marquee ───────────────────────────────────────────────────────────────────
function Marquee() {
  const items = ['No mixed signals', 'Honest intentions', 'Real connections', 'Verified profiles', 'Reputation scores', 'No ghosting', 'Structured openers', 'Profile resets', 'Mutual compatibility', 'Say what you want'];
  return (
    <div style={{ overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 0', background: 'rgba(255,255,255,0.02)' }}>
      <div style={{ display: 'flex', gap: 48, animation: 'marquee 30s linear infinite', width: 'max-content' }}>
        {[...items, ...items].map((item, i) => (
          <span key={i} style={{ fontSize: 13, color: C.muted, fontWeight: 500, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#a78bfa', display: 'inline-block' }} />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Features ──────────────────────────────────────────────────────────────────
function Features() {
  const features = [
    { icon: '🔍', title: 'Intentions lead, photos follow', desc: '"Looking for…" is the first thing on every card. You know what someone wants before anything else. No mixed signals.', color: '#a78bfa', grad: 'linear-gradient(135deg,rgba(167,139,250,0.15),rgba(167,139,250,0.05))' },
    { icon: '⭐', title: 'A reputation you earn', desc: 'After connections end, both people rate anonymously. Your standing shows as a tier — Newcomer to Candid — earned from how you actually treat people.', color: '#fbbf24', grad: 'linear-gradient(135deg,rgba(251,191,36,0.15),rgba(251,191,36,0.05))' },
    { icon: '⏳', title: 'Your words refresh every 30 days', desc: 'People change. Every month you re-confirm your intention, bio, and dealbreakers — keep them or rewrite them. Your reputation carries over.', color: '#f472b6', grad: 'linear-gradient(135deg,rgba(244,114,182,0.15),rgba(244,114,182,0.05))' },
    { icon: '💬', title: 'Structured first messages', desc: 'No "hey." No copy-paste openers. Your first message must answer why you two are compatible. Minimum 50 characters.', color: '#34d399', grad: 'linear-gradient(135deg,rgba(52,211,153,0.15),rgba(52,211,153,0.05))' },
    { icon: '🤝', title: 'No ghosting, by design', desc: 'Ignored requests expire and can cost reputation. Ending a connection sends a kind closing message — then you both rate each other. No vanishing.', color: '#60a5fa', grad: 'linear-gradient(135deg,rgba(96,165,250,0.15),rgba(96,165,250,0.05))' },
    { icon: '✓', title: 'Selfie verification', desc: 'A live selfie, reviewed by a real human before the verified badge is granted. No catfishing. No bots. Just real people who mean what they say.', color: '#fb923c', grad: 'linear-gradient(135deg,rgba(251,146,60,0.15),rgba(251,146,60,0.05))' },
  ];

  return (
    <section id="features" className="section-pad" style={{ padding: '120px 40px', position: 'relative', maxWidth: 1200, margin: '0 auto' }}>
      <FadeUp>
        <div style={{ textAlign: 'center', marginBottom: 72 }}>
          <div style={{ display: 'inline-block', background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 50, padding: '5px 16px', fontSize: 12, color: '#c4b5fd', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 20 }}>Features</div>
          <h2 className="section-h2" style={{ fontFamily: 'Fraunces,serif', fontSize: 56, fontWeight: 900, letterSpacing: -3, lineHeight: 1, marginBottom: 18 }}>
            Built for <span className="grad-text">honest people</span>
          </h2>
          <p style={{ fontSize: 18, color: C.muted, maxWidth: 500, margin: '0 auto', lineHeight: 1.7, fontWeight: 300 }}>
            Every feature is designed to make honesty the default — not the exception.
          </p>
        </div>
      </FadeUp>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 20 }}>
        {features.map((f, i) => (
          <FadeUp key={f.title} delay={i * 80}>
            <div className="feature-card" style={{ background: f.grad }}>
              <div style={{ width: 52, height: 52, borderRadius: 16, background: `${f.color}20`, border: `1px solid ${f.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 18 }}>{f.icon}</div>
              <h3 style={{ fontFamily: 'Fraunces,serif', fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 10, letterSpacing: -.5 }}>{f.title}</h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, fontWeight: 300 }}>{f.desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

// ── How it works ──────────────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    { n: '01', title: 'Set your intention', desc: 'Before anything else, you say what you actually want. Not a checkbox — your own words. This is the first thing anyone sees.', color: '#a78bfa' },
    { n: '02', title: 'Get verified', desc: 'Take a live selfie — a real human on our team reviews it before your verified badge appears. No fakes, no bots.', color: '#f472b6' },
    { n: '03', title: 'Browse by intention', desc: '"Looking for…" leads every card, so you know what someone wants up front. You see everyone whose preferences match yours both ways — with the most compatible intentions surfaced first.', color: '#fbbf24' },
    { n: '04', title: 'Send a real opener', desc: 'No "hey." Write why you two are compatible — 50 characters minimum. It takes 30 seconds and filters out everyone who isn\'t serious.', color: '#34d399' },
    { n: '05', title: 'Build your reputation', desc: 'After each connection ends, both people rate each other anonymously. Climb from Newcomer to Candid — your tier shows on your profile.', color: '#60a5fa' },
  ];

  return (
    <section id="how-it-works" className="section-pad" style={{ padding: '120px 40px', position: 'relative' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, rgba(167,139,250,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="hiw-grid" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 100, alignItems: 'start', position: 'relative', zIndex: 1 }}>
        {/* Left */}
        <div>
          <FadeUp>
            <div style={{ display: 'inline-block', background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.2)', borderRadius: 50, padding: '5px 16px', fontSize: 12, color: '#f9a8d4', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 20 }}>How it works</div>
            <h2 className="section-h2" style={{ fontFamily: 'Fraunces,serif', fontSize: 52, fontWeight: 900, letterSpacing: -3, lineHeight: 1.05, marginBottom: 20 }}>
              From stranger to <span className="grad-text">connection</span> in 5 steps
            </h2>
            <p style={{ fontSize: 17, color: C.muted, lineHeight: 1.8, fontWeight: 300 }}>
              The whole thing takes under 5 minutes. Then you're in a pool of verified, honest people who actually mean what they say.
            </p>
          </FadeUp>
        </div>

        {/* Right — Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {steps.map((step, i) => (
            <FadeUp key={step.n} delay={i * 100}>
              <div style={{ display: 'flex', gap: 20, paddingBottom: 32, position: 'relative' }}>
                {i < steps.length - 1 && <div className="step-line" />}
                <div style={{ width: 46, height: 46, borderRadius: '50%', background: `${step.color}15`, border: `2px solid ${step.color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontFamily: 'DM Mono,monospace', fontWeight: 700, color: step.color, flexShrink: 0 }}>{step.n}</div>
                <div style={{ paddingTop: 10 }}>
                  <h3 style={{ fontFamily: 'Fraunces,serif', fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 6, letterSpacing: -.5 }}>{step.title}</h3>
                  <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, fontWeight: 300 }}>{step.desc}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────
function CTA({ onSignIn }) {
  return (
    <section className="section-pad" style={{ padding: '120px 40px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle,rgba(167,139,250,0.15) 0%,transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
        <FadeUp>
          <div className="glass glow-border cta-card" style={{ borderRadius: 32, padding: '72px 60px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: '50%', height: '80%', borderRadius: '50%', background: 'radial-gradient(circle,rgba(244,114,182,0.15) 0%,transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ fontSize: 52, marginBottom: 20, animation: 'float 4s ease-in-out infinite' }}>🕯️</div>
            <h2 className="section-h2" style={{ fontFamily: 'Fraunces,serif', fontSize: 56, fontWeight: 900, letterSpacing: -3, lineHeight: 1, marginBottom: 20 }}>
              Ready to be <span className="grad-text">candid?</span>
            </h2>
            <p style={{ fontSize: 18, color: C.muted, lineHeight: 1.8, marginBottom: 40, fontWeight: 300 }}>
              Say what you actually want — and meet people who want the same thing.
            </p>
            <div className="hero-cta-row" style={{ display: 'flex', gap: 14, justifyContent: 'center', marginBottom: 24 }}>
              <DownloadButton style={{ fontSize: 16, padding: '16px 40px' }} />
              <a className="btn-ghost" href="#features" style={{ fontSize: 16, padding: '16px 28px', textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}>Learn more</a>
            </div>
            <p style={{ fontSize: 12, color: C.dim }}>Free to join · iOS first · Selfie-verified community</p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}



// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '48px 40px' }}>
      <div className="footer-row" style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: 'Fraunces,serif', fontSize: 22, fontWeight: 900, letterSpacing: -1, marginBottom: 6 }}>
            <span className="grad-text">candid.</span>
          </div>
          <p style={{ fontSize: 13, color: C.dim }}>Say what you actually want.</p>
        </div>
        <div className="footer-links" style={{ display: 'flex', gap: 32 }}>
          {[
            { label: 'Privacy', to: '/privacy' },
            { label: 'Terms', to: '/terms' },
            { label: 'Contact', to: '/contact' },
          ].map(l => (
            <Link key={l.label} to={l.to} className="nav-link" style={{ fontSize: 13, color: 'inherit', textDecoration: 'none' }}>
              {l.label}
            </Link>
          ))}
        </div>
        <p className="footer-copyright" style={{ fontSize: 12, color: C.dim }}>© 2026 Candid. All rights reserved.</p>
      </div>
    </footer>
  );
}

// ── Mystery Landing (pre-launch) ──────────────────────────────────────────────
function MysteryLanding() {
  const waitlistCount = useWaitlistCount();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState('email'); // 'email' | 'details' | 'done' | 'duplicate' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  // Optional details state
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [waitlistId, setWaitlistId] = useState(null);

  const validEmail = /^\S+@\S+\.\S+$/.test(email);

  const handleEmailSubmit = async () => {
    if (!validEmail || submitting) return;
    setSubmitting(true);
    try {
      const normalized = email.trim().toLowerCase();
      // NOTE: no .select() after insert — RLS only lets admins READ waitlist
      // rows (so the email list can't be scraped), and asking Postgres to
      // return the inserted row counts as a read. The follow-up details
      // update is keyed on the email instead of the row id.
      const { error } = await supabase
        .from('waitlist')
        .insert({ email: normalized, name: 'pending' });
      if (error) {
        if (error.code === '23505' || /duplicate/i.test(error.message)) {
          setStage('duplicate');
        } else {
          setStage('error');
          setErrorMsg(error.message);
        }
      } else {
        setWaitlistId(normalized);
        setStage('details');
      }
    } catch (e) {
      setStage('error');
      setErrorMsg(e?.message || 'Something went wrong');
    }
    setSubmitting(false);
  };

  const handleDetailsSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await supabase
        .from('waitlist')
        .update({
          name: name.trim() || 'friend',
          city: city.trim() || null,
        })
        .eq('email', waitlistId);
    } catch {}
    setStage('done');
    setSubmitting(false);
  };

  const handleSkipDetails = () => {
    setStage('done');
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
      <style>{css}</style>
      <AuroraBg fixed />
      <div style={{ position: 'fixed', inset: 0, opacity: .025, backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")", backgroundSize: '150px', pointerEvents: 'none', zIndex: 0 }} />

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 720, width: '100%' }}>

          {/* Top: logo */}
          <div style={{ marginBottom: 72 }}>
            <span style={{ fontFamily: 'Fraunces, serif', fontSize: 32, fontWeight: 700, letterSpacing: -1, whiteSpace: 'nowrap' }} className="grad-text mystery-logo">candid.</span>
          </div>

          {stage === 'email' && (
            <>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 50, padding: '6px 16px', marginBottom: 28, animation: 'fadeUp .6s ease both' }}>
                <span style={{ position: 'relative', display: 'inline-block', width: 8, height: 8 }}>
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#a78bfa', animation: 'ping 1.6s infinite' }} />
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#a78bfa' }} />
                </span>
                <span style={{ fontSize: 12, color: '#c4b5fd', fontWeight: 600, letterSpacing: .5 }}>Coming soon · iOS first</span>
              </div>
              <h1 className="mystery-hero" style={{ fontFamily: 'Fraunces, serif', fontSize: 84, fontWeight: 500, lineHeight: 1.08, letterSpacing: -4, color: C.text, margin: '0 0 32px', animation: 'fadeUp .7s ease .05s both' }}>
                The dating app<br />you <em style={{ fontStyle: 'italic' }} className="grad-text">wish existed</em>.
              </h1>
              <p style={{ fontSize: 17, color: C.muted, lineHeight: 1.7, fontWeight: 300, maxWidth: 460, margin: '0 0 48px', animation: 'fadeUp .6s ease .1s both' }}>
                We're building something different. Join the waitlist if you're curious.
              </p>

              <div className="waitlist-form-row" style={{ display: 'flex', gap: 8, animation: 'fadeUp .6s ease .2s both' }}>
                <input
                  type="email"
                  className="waitlist-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()}
                  placeholder="your@email.com"
                  style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '15px 18px', fontSize: 15, color: C.text, fontFamily: 'inherit', outline: 'none' }}
                />
                <button
                  className="btn-primary waitlist-btn"
                  onClick={handleEmailSubmit}
                  disabled={!validEmail || submitting}
                  style={{ padding: '0 24px', fontSize: 14, opacity: !validEmail || submitting ? 0.5 : 1, cursor: !validEmail || submitting ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  {submitting ? 'Adding…' : 'Reserve →'}
                </button>
              </div>
              <p style={{ fontSize: 12, color: C.dim, marginTop: 12, textAlign: 'center' }}>
                No spam. Just one email when iOS is ready.
                {waitlistCount !== null && <> · <strong style={{ color: C.muted }}>{waitlistCount.toLocaleString()}</strong> already in line.</>}
              </p>
            </>
          )}

          {stage === 'details' && (
            <div style={{ animation: 'fadeUp .5s ease both' }}>
              <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 48, fontWeight: 500, lineHeight: 1.05, letterSpacing: -2, color: C.text, margin: '0 0 16px' }}>
                <span className="grad-text">You're in.</span>
              </h1>
              <p style={{ fontSize: 16, color: C.muted, lineHeight: 1.6, fontWeight: 300, margin: '0 0 40px' }}>
                One more thing — if you've got 30 seconds. It'll help us launch better.
              </p>

              <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
                <input
                  className="waitlist-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="What should we call you?"
                  style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '13px 16px', fontSize: 14, color: C.text, fontFamily: 'inherit', outline: 'none' }}
                />
                <input
                  className="waitlist-input"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  placeholder="What city are you in?"
                  style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '13px 16px', fontSize: 14, color: C.text, fontFamily: 'inherit', outline: 'none' }}
                />

              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn-primary"
                  onClick={handleDetailsSubmit}
                  disabled={submitting}
                  style={{ flex: 1, opacity: submitting ? 0.5 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                >
                  {submitting ? 'Saving…' : 'Submit'}
                </button>
                <button
                  className="btn-ghost"
                  onClick={handleSkipDetails}
                  style={{ padding: '0 24px', fontSize: 13 }}
                >
                  Skip
                </button>
              </div>
            </div>
          )}

          {stage === 'done' && (
            <div style={{ textAlign: 'center', animation: 'fadeUp .5s ease both' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>✨</div>
              <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 48, fontWeight: 500, letterSpacing: -2, margin: '0 0 16px' }}>
                <span className="grad-text">Thanks for joining.</span>
              </h1>
              <p style={{ fontSize: 16, color: C.muted, lineHeight: 1.7, fontWeight: 300, maxWidth: 380, margin: '0 auto' }}>
                We'll be in touch the moment iOS is ready. No spam, no other nonsense.
              </p>
            </div>
          )}

          {stage === 'duplicate' && (
            <div style={{ textAlign: 'center', animation: 'fadeUp .5s ease both' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>👋</div>
              <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 48, fontWeight: 500, letterSpacing: -2, margin: '0 0 16px' }}>
                <span className="grad-text">You're already in.</span>
              </h1>
              <p style={{ fontSize: 16, color: C.muted, lineHeight: 1.7, fontWeight: 300, maxWidth: 380, margin: '0 auto' }}>
                Sit tight. We'll be in touch.
              </p>
            </div>
          )}

          {stage === 'error' && (
            <div style={{ textAlign: 'center', animation: 'fadeUp .5s ease both' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>😕</div>
              <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 36, fontWeight: 500, letterSpacing: -1.5, margin: '0 0 12px' }}>
                Something broke.
              </h1>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, margin: '0 0 24px' }}>
                {errorMsg}<br />Email us at justcandid.app@gmail.com instead?
              </p>
              <button className="btn-ghost" onClick={() => { setStage('email'); setErrorMsg(''); }}>Try again</button>
            </div>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
}

// ── Main Landing Page ─────────────────────────────────────────────────────────
export default function LandingPage({ onSignIn }) {
  if (!LAUNCHED) {
    return <MysteryLanding />;
  }
  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text }}>
      <style>{css}</style>
      <Nav onSignIn={onSignIn} />
      <Hero onSignIn={onSignIn} />
      <Marquee />
      <Features />
      <HowItWorks />
      <CTA onSignIn={onSignIn} />
      <Footer />
    </div>
  );
}